import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { twoFactorService, type TwoFactorMethodKind } from '../../services/twoFactorService';
import { useNotifications } from '../feedback/NotificationSystem';
import type { LearnerSettingsSectionTheme } from './learnerSettingsSectionThemes';
import { methodLabel } from './TwoFactorMethodTabs';
import { openChannelBotDeepLink } from '../../utils/openChannelBotDeepLink';
import { isMobileDevice } from '../../utils/isMobileDevice';
import { resolveTwoFactorChannelMessage } from '../../utils/twoFactorChannelMessages';
import { ChannelBotDesktopLinkPanel } from '../twoFactor/ChannelBotDesktopLinkPanel';
import { isStoredE164Phone, toStoredE164Input } from '../../utils/iranPhoneNumber';

type ChannelMethod = 'email' | 'telegram' | 'bale';

interface LearnerChannelTwoFactorPanelProps {
  theme: LearnerSettingsSectionTheme;
  method: ChannelMethod;
  enabled: boolean;
  activeMethod: TwoFactorMethodKind;
  telegramLinked: boolean;
  baleLinked: boolean;
  maskedPhone?: string | null;
  profilePhoneNumber?: string | null;
  twoFactorChannelPhone?: string | null;
  accountEmail?: string | null;
  telegramBotUsername?: string | null;
  baleBotUsername?: string | null;
  onStatusChanged: () => void | Promise<void>;
}

function resolveDefaultPhone(twoFactorChannelPhone?: string | null, profilePhoneNumber?: string | null): string {
  return toStoredE164Input(twoFactorChannelPhone) || toStoredE164Input(profilePhoneNumber);
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === 'string' && msg.trim()) {
      return msg;
    }
  }
  return fallback;
}

export const LearnerChannelTwoFactorPanel: React.FC<LearnerChannelTwoFactorPanelProps> = ({
  theme,
  method,
  enabled,
  activeMethod,
  telegramLinked,
  baleLinked,
  maskedPhone,
  profilePhoneNumber,
  twoFactorChannelPhone,
  accountEmail,
  telegramBotUsername,
  baleBotUsername,
  onStatusChanged
}) => {
  const { t } = useTranslation('settings');
  const { showSuccess, showError } = useNotifications();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [deepLink, setDeepLink] = useState('');
  const [polling, setPolling] = useState(false);
  const [localChannelLinked, setLocalChannelLinked] = useState(false);
  const [devChatId, setDevChatId] = useState('');
  const [linkWaitExpired, setLinkWaitExpired] = useState(false);
  const isDev = import.meta.env.DEV;
  const linkTokenRef = useRef('');
  const deepLinkRef = useRef('');
  const pollGenerationRef = useRef(0);
  const linkTimeoutNotifiedRef = useRef(false);
  const linkFetchStartedRef = useRef(false);

  const channelLinked =
    method === 'telegram'
      ? telegramLinked || localChannelLinked
      : method === 'bale'
        ? baleLinked || localChannelLinked
        : true;

  const isThisMethodActive = enabled && activeMethod === method;
  const anotherMethodActive = enabled && activeMethod !== method && activeMethod !== 'none';

  useEffect(() => {
    setCode('');
    setPhone(resolveDefaultPhone(twoFactorChannelPhone, profilePhoneNumber));
    setDeepLink('');
    setDevChatId('');
    setLocalChannelLinked(false);
    setLinkWaitExpired(false);
    linkTokenRef.current = '';
    deepLinkRef.current = '';
    linkFetchStartedRef.current = false;
    linkTimeoutNotifiedRef.current = false;
    pollGenerationRef.current += 1;
    setPolling(false);
  }, [method, profilePhoneNumber, twoFactorChannelPhone]);

  useEffect(() => {
    if ((method === 'telegram' && telegramLinked) || (method === 'bale' && baleLinked)) {
      setLocalChannelLinked(false);
    }
  }, [method, telegramLinked, baleLinked]);

  const fetchBotLink = useCallback(async (channel: 'telegram' | 'bale') => {
    if (linkTokenRef.current && deepLinkRef.current) {
      return { linkToken: linkTokenRef.current, deepLink: deepLinkRef.current };
    }
    const link =
      channel === 'telegram'
        ? await twoFactorService.getTelegramLink()
        : await twoFactorService.getBaleLink();
    linkTokenRef.current = link.linkToken;
    deepLinkRef.current = link.deepLink;
    setDeepLink(link.deepLink);
    return link;
  }, []);

  const pollLink = useCallback(
    async (channel: 'telegram' | 'bale', token: string) => {
      const generation = ++pollGenerationRef.current;
      setPolling(true);
      setLinkWaitExpired(false);
      try {
        for (let i = 0; i < 40; i++) {
          if (pollGenerationRef.current !== generation) {
            return;
          }
          try {
            const st =
              channel === 'telegram'
                ? await twoFactorService.getTelegramLinkStatus(token)
                : await twoFactorService.getBaleLinkStatus(token);
            if (st.linked) {
              if (pollGenerationRef.current !== generation) {
                return;
              }
              linkTimeoutNotifiedRef.current = false;
              setLocalChannelLinked(true);
              showSuccess(
                channel === 'telegram' ? t('security.telegramLinked') : t('security.baleLinked')
              );
              await onStatusChanged();
              return;
            }
          } catch {
            // Ignore transient status check failures while waiting for the bot link.
          }
          await new Promise((r) => setTimeout(r, 3000));
        }
        if (pollGenerationRef.current !== generation || linkTimeoutNotifiedRef.current) {
          return;
        }
        linkTimeoutNotifiedRef.current = true;
        setLinkWaitExpired(true);
        showError(t('security.linkTimedOut'), { context: 'channel-bot-link' });
      } finally {
        if (pollGenerationRef.current === generation) {
          setPolling(false);
        }
      }
    },
    [onStatusChanged, showError, showSuccess, t]
  );

  const beginLinkPolling = useCallback(
    (channel: 'telegram' | 'bale') => {
      linkTimeoutNotifiedRef.current = false;
      setLinkWaitExpired(false);
      const token = linkTokenRef.current;
      if (!token) {
        return;
      }
      void pollLink(channel, token);
    },
    [pollLink]
  );

  useEffect(() => {
    if (channelLinked || isMobileDevice()) {
      return;
    }
    if (method !== 'telegram' && method !== 'bale') {
      return;
    }
    if (linkFetchStartedRef.current || linkTokenRef.current) {
      return;
    }

    linkFetchStartedRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        await fetchBotLink(method);
      } catch {
        if (!cancelled) {
          linkFetchStartedRef.current = false;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [channelLinked, fetchBotLink, method]);

  const startLink = async (channel: 'telegram' | 'bale') => {
    setBusy(true);
    try {
      const link = linkTokenRef.current
        ? { linkToken: linkTokenRef.current, deepLink: deepLinkRef.current }
        : await fetchBotLink(channel);
      if (!link?.linkToken) {
        showError(t('security.botLinkStartFailed'));
        return;
      }
      if (isMobileDevice() && link.deepLink) {
        await openChannelBotDeepLink(link.deepLink, channel);
      }
      void pollLink(channel, link.linkToken);
    } catch (e) {
      showError(e instanceof Error ? e.message : t('security.botLinkStartFailed'));
    } finally {
      setBusy(false);
    }
  };

  const checkLinkAfterStart = async (channel: 'telegram' | 'bale') => {
    setBusy(true);
    try {
      if (!linkTokenRef.current) {
        await fetchBotLink(channel);
      }
      beginLinkPolling(channel);
    } catch (e) {
      showError(e instanceof Error ? e.message : t('security.botLinkStartFailed'));
    } finally {
      setBusy(false);
    }
  };

  const sendSetupCode = async () => {
    if (method === 'telegram' || method === 'bale') {
      let linked = channelLinked;
      if (!linked) {
        const status = await twoFactorService.getStatus();
        linked = method === 'telegram' ? status.telegramLinked : status.baleLinked;
        if (linked) {
          setLocalChannelLinked(true);
          await onStatusChanged();
        }
      }
      if (!linked) {
        showError(
          method === 'bale' ? t('security.linkAccountFirstBale') : t('security.linkAccountFirstTelegram')
        );
        return;
      }
    }
    setBusy(true);
    try {
      const r = await twoFactorService.sendChannelSetupCode(method, toStoredE164Input(phone) || undefined);
      if (r.success) {
        const resolved = resolveTwoFactorChannelMessage(r.message, t, 'settings', method);
        showSuccess(resolved.message, { duration: resolved.duration ?? 5000 });
      } else {
        showError(r.message || t('security.codeSendFailed'));
      }
    } catch (e) {
      showError(apiErrorMessage(e, t('security.codeSendFailed')));
    } finally {
      setBusy(false);
    }
  };

  const enableChannel = async () => {
    setBusy(true);
    try {
      const r = await twoFactorService.enableChannel(method, code);
      if (r.success) {
        showSuccess(r.message || t('security.twoFactorEnabled'));
        setCode('');
        onStatusChanged();
      } else {
        showError(r.message || t('security.invalidCode'));
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : t('security.enableFailed'));
    } finally {
      setBusy(false);
    }
  };

  const disableChannel = async () => {
    setBusy(true);
    try {
      const r = await twoFactorService.disable(code, null);
      if (r.success) {
        showSuccess(t('security.twoFactorDisabled'));
        setCode('');
        onStatusChanged();
      } else {
        showError(r.message || t('security.disableFailed'));
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : t('security.disableFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (anotherMethodActive) {
    return (
      <div className="space-y-2 rounded-xl border border-slate-200/90 bg-white/60 p-4 dark:border-gray-600 dark:bg-gray-900/40">
        <p className="text-xs text-slate-600 dark:text-slate-300">
          {t('security.channelAnotherMethodActive', {
            active: methodLabel(activeMethod, t),
            method: methodLabel(method, t),
          })}
        </p>
      </div>
    );
  }

  if (isThisMethodActive) {
    return (
      <div className="space-y-3 rounded-xl border border-slate-200/90 bg-white/60 p-4 dark:border-gray-600 dark:bg-gray-900/40">
        <p className="text-xs text-slate-600 dark:text-slate-300">
          {t('security.channelActiveMethod', {
            method: methodLabel(method, t),
            phone: maskedPhone ? ` (${maskedPhone})` : '',
          })}
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={() => void sendSetupCode()}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm dark:border-gray-600"
        >
          {t('security.sendCodeToDisable')}
        </button>
        <input
          type="text"
          inputMode="numeric"
          maxLength={8}
          placeholder={t('security.verificationCodePlaceholder')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        />
        <button
          type="button"
          disabled={busy || code.length < 6}
          onClick={() => void disableChannel()}
          className={theme.saveButton}
        >
          {busy ? <Loader2 className="inline h-4 w-4 animate-spin" /> : t('security.turnOff2fa')}
        </button>
        <p className="text-[11px] text-slate-500">{t('security.sendFreshCodeToDisable', { method })}</p>
        {method === 'email' && accountEmail?.trim() && (
          <p className="text-[11px] text-slate-600 dark:text-slate-400">
            {t('security.codesSentTo', { email: accountEmail.trim() })}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-slate-200/90 bg-white/60 p-4 dark:border-gray-600 dark:bg-gray-900/40">
      {method === 'email' && (
        <p className="text-xs text-slate-600 dark:text-slate-400">
          {accountEmail?.trim() ? (
            <Trans
              i18nKey="security.emailCodesWillBeSent"
              ns="settings"
              values={{ email: accountEmail.trim() }}
              components={{ strong: <strong className="break-all text-slate-800 dark:text-slate-100" /> }}
            />
          ) : (
            t('security.emailCodeHintFallback')
          )}
        </p>
      )}

      {(method === 'telegram' || method === 'bale') && (
        <>
          <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400">
            {t('security.phoneNumberLabel')}
          </label>
          <input
            type="tel"
            placeholder={t('security.phonePlaceholder')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onBlur={() => {
              const stored = toStoredE164Input(phone);
              if (!stored || isStoredE164Phone(stored)) {
                setPhone(stored);
              }
            }}
            className="w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          />
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            {profilePhoneNumber?.trim()
              ? t('security.phonePrefilledFromProfile')
              : t('security.phoneSaveToProfile')}
          </p>
          {method === 'telegram' && !channelLinked && (
            <ol className="list-decimal list-inside space-y-1 text-[11px] text-slate-600 dark:text-slate-400 max-w-prose">
              <li>
                {isMobileDevice() ? (
                  <Trans i18nKey="security.telegramStep1" ns="settings" components={{ strong: <strong /> }} />
                ) : (
                  <Trans i18nKey="security.telegramStep1Desktop" ns="settings" components={{ strong: <strong /> }} />
                )}
              </li>
              <li>
                {isMobileDevice() ? (
                  <Trans i18nKey="security.telegramStep2" ns="settings" components={{ strong: <strong /> }} />
                ) : (
                  t('security.telegramStep2Desktop')
                )}
              </li>
              <li>
                {polling ? t('security.telegramStep3Polling') : t('security.telegramStep3Idle')}
              </li>
            </ol>
          )}
          {method === 'bale' && !channelLinked && (
            <ol className="list-decimal list-inside space-y-1 text-[11px] text-slate-600 dark:text-slate-400 max-w-prose">
              <li>
                {isMobileDevice() ? (
                  <Trans i18nKey="security.baleStep1" ns="settings" components={{ strong: <strong /> }} />
                ) : (
                  <Trans i18nKey="security.baleStep1Desktop" ns="settings" components={{ strong: <strong /> }} />
                )}
              </li>
              <li>
                {isMobileDevice() ? (
                  <Trans i18nKey="security.baleStep2" ns="settings" components={{ strong: <strong /> }} />
                ) : (
                  t('security.baleStep2Desktop')
                )}
              </li>
              <li>
                {polling ? t('security.baleStep3Polling') : t('security.baleStep3Idle')}
              </li>
            </ol>
          )}
          {!channelLinked && !isMobileDevice() && deepLink && (
            <ChannelBotDesktopLinkPanel
              method={method}
              deepLink={deepLink}
              polling={polling}
              linkWaitExpired={linkWaitExpired}
              showLocalhostHint={isDev}
              checkDisabled={busy}
              onCheckLink={() => void checkLinkAfterStart(method)}
            />
          )}
          <p className="text-[11px] text-slate-500">
            {t('security.botHandle', {
              channel: method === 'telegram' ? t('security.methodTelegram') : t('security.methodBaleShort'),
              username: method === 'telegram' ? telegramBotUsername || 'bot' : baleBotUsername || 'bot',
              linked: channelLinked ? t('security.botLinkedSuffix') : '',
            })}
          </p>
          {!channelLinked && isMobileDevice() && (
            <div className="flex flex-col gap-2 w-full">
              <button
                type="button"
                disabled={busy || polling}
                onClick={() => void startLink(method)}
                className={theme.saveButton}
              >
                {polling
                  ? method === 'telegram'
                    ? t('security.waitingInTelegram')
                    : t('security.waitingInBale')
                  : method === 'telegram'
                    ? t('security.openBotTelegram')
                    : t('security.openBotBale')}
              </button>
              {deepLink && (
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  <Trans
                    i18nKey="security.openBotFallback"
                    ns="settings"
                    values={{ channel: method === 'telegram' ? t('security.methodTelegram') : t('security.methodBaleShort') }}
                    components={{
                      link: (
                        <a
                          href={deepLink}
                          target="_blank"
                          rel="noreferrer"
                          className="text-teal-700 underline break-all dark:text-teal-400"
                        />
                      ),
                    }}
                  />
                </p>
              )}
            </div>
          )}
          {!channelLinked && isDev && (method === 'telegram' || method === 'bale') && (
            <details className="rounded-lg border border-dashed border-amber-400/80 bg-amber-50/60 p-3 dark:border-amber-700/60 dark:bg-amber-950/20" open>
              <summary className="flex cursor-pointer flex-wrap items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-200">
                <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-950 dark:bg-amber-800 dark:text-amber-50">
                  {t('security.developmentOnlyBadge')}
                </span>
                {method === 'telegram'
                  ? t('security.linkWithTelegramIdSummary')
                  : t('security.linkWithBaleIdSummary')}
              </summary>
              <p className="mt-2 text-[11px] text-slate-600 dark:text-slate-400">
                {method === 'telegram' ? (
                  <Trans
                    i18nKey="security.linkWithTelegramIdHint"
                    ns="settings"
                    components={{ strong: <strong /> }}
                  />
                ) : (
                  <Trans
                    i18nKey="security.linkWithBaleIdHint"
                    ns="settings"
                    components={{
                      code: <code className="text-[10px]" />,
                      strong: <strong />
                    }}
                  />
                )}
              </p>
              <input
                type="text"
                inputMode="numeric"
                placeholder={
                  method === 'telegram'
                    ? t('security.chatIdPlaceholderTelegram')
                    : t('security.chatIdPlaceholderBale')
                }
                value={devChatId}
                onChange={(e) => setDevChatId(e.target.value.replace(/[^\d]/g, ''))}
                className="mt-2 w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
              />
              <button
                type="button"
                disabled={busy || !devChatId.trim()}
                onClick={() => {
                  void (async () => {
                    setBusy(true);
                    try {
                      const id = devChatId.trim();
                      if (!id || !/^\d+$/.test(id)) {
                        showError(
                          method === 'telegram'
                            ? t('security.enterTelegramUserId')
                            : t('security.enterBaleUserId')
                        );
                        return;
                      }
                      if (method === 'telegram') {
                        await twoFactorService.linkTelegramChatId(Number(id), toStoredE164Input(phone) || undefined);
                        showSuccess(t('security.telegramLinked'));
                      } else {
                        await twoFactorService.linkBaleUserId(id, toStoredE164Input(phone) || undefined);
                        showSuccess(t('security.baleLinked'));
                      }
                      setDevChatId('');
                      setLocalChannelLinked(true);
                      await onStatusChanged();
                    } catch (e) {
                      const msg =
                        e instanceof Error
                          ? e.message
                          : typeof e === 'object' && e !== null && 'message' in e
                            ? String((e as { message: unknown }).message)
                            : t('security.linkFailed');
                      showError(
                        msg.includes('404') || msg.toLowerCase().includes('not found')
                          ? t('security.manualLinkDevOnly')
                          : msg
                      );
                    } finally {
                      setBusy(false);
                    }
                  })();
                }}
                className="mt-2 rounded-lg border border-slate-400 px-3 py-1.5 text-xs font-semibold"
              >
                {t('security.linkWithIdButton')}
              </button>
            </details>
          )}
        </>
      )}

      <button type="button" disabled={busy} onClick={() => void sendSetupCode()} className={theme.saveButton}>
        {t('security.sendVerificationCode')}
      </button>

      <input
        type="text"
        inputMode="numeric"
        maxLength={8}
        placeholder={t('security.sixDigitCodePlaceholder')}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
      />
      <button
        type="button"
        disabled={busy || code.length < 6}
        onClick={() => void enableChannel()}
        className={theme.saveButton}
      >
        {busy ? (
          <Loader2 className="inline h-4 w-4 animate-spin" />
        ) : (
          t('security.enableChannel2fa', { method: methodLabel(method, t) })
        )}
      </button>
    </div>
  );
};
